import * as THREE from "three";
import type { Direction } from "../types/grid.js";
import { devLog } from "../debug/devLog.js";

const CELL_SIZE = 1; // world units per grid cell
const TWEEN_DURATION_MS = 220; // milliseconds for move/rotate animation

/**
 * Drives smooth camera movement and rotation via sine ease-in-out tweening.
 * Logical state (grid position, facing) updates instantly;
 * the visual camera interpolates to match.
 */
export class CameraController {
  private tweenFrom = new THREE.Vector3();
  private tweenTo = new THREE.Vector3();
  private quatFrom = new THREE.Quaternion();
  private quatTo = new THREE.Quaternion();
  private tweenElapsed = 0;
  private tweening = false;
  private onComplete?: () => void;
  /**
   * Multiplier applied to TWEEN_DURATION_MS (E03). 1.0 = normal speed,
   * 2.0 = half speed (sneak). The sneak subscription lives in `main.tsx`.
   */
  private durationMultiplier = 1;

  constructor(readonly camera: THREE.PerspectiveCamera) {}

  /** Called once each time a move/rotate tween finishes. */
  setOnTweenComplete(cb: () => void): void {
    this.onComplete = cb;
  }

  /**
   * Scale the tween duration. 1.0 is the default; the sneak toggle (E03)
   * raises this to 2.0 to halve movement speed.
   */
  setTweenDurationMultiplier(mult: number): void {
    this.durationMultiplier = Math.max(0.01, mult);
  }

  /** Snap camera to a grid position/facing with no animation (used at level load). */
  snapTo(x: number, z: number, facing: Direction): void {
    this.camera.position.set(x * CELL_SIZE, 0.5, z * CELL_SIZE);
    this.camera.rotation.set(0, -facing, 0);
    this.tweening = false;
  }

  /** Begin a smooth move to target grid position. */
  moveTo(x: number, z: number): void {
    if (this.tweening) {
      devLog("camera:moveTo-while-tweening", { x, z, elapsed: this.tweenElapsed });
    }
    this.tweenFrom.copy(this.camera.position);
    this.tweenTo.set(x * CELL_SIZE, 0.5, z * CELL_SIZE);
    this.quatFrom.copy(this.camera.quaternion);
    this.quatTo.copy(this.camera.quaternion);
    this.tweenElapsed = 0;
    this.tweening = true;
    devLog("camera:tween-start", { kind: "move", to: { x, z } });
  }

  /** Begin a smooth rotation to target facing direction. */
  rotateTo(facing: Direction): void {
    if (this.tweening) {
      devLog("camera:rotateTo-while-tweening", { facing, elapsed: this.tweenElapsed });
    }
    const target = new THREE.Euler(0, -facing, 0);
    this.tweenFrom.copy(this.camera.position);
    this.tweenTo.copy(this.camera.position);
    this.quatFrom.copy(this.camera.quaternion);
    this.quatTo.setFromEuler(target);
    this.tweenElapsed = 0;
    this.tweening = true;
    devLog("camera:tween-start", { kind: "rotate", facing });
  }

  /** Returns true while an animation is in progress. */
  get isTweening(): boolean {
    return this.tweening;
  }

  /** Advance the tween by `delta` seconds. Call once per frame. */
  tick(delta: number): void {
    if (!this.tweening) return;

    this.tweenElapsed += delta * 1000;
    const t = Math.min(this.tweenElapsed / (TWEEN_DURATION_MS * this.durationMultiplier), 1);
    const ease = (1 - Math.cos(t * Math.PI)) / 2; // sine ease-in-out

    this.camera.position.lerpVectors(this.tweenFrom, this.tweenTo, ease);
    this.camera.quaternion.slerpQuaternions(this.quatFrom, this.quatTo, ease);

    if (t >= 1) {
      this.tweening = false;
      devLog("camera:tween-end");
      this.onComplete?.();
    }
  }
}
